/**
 * transformedArc — arc / bulge-span geometry mapped through an {@link Affine2D},
 * for every PDF converter that emits curves in a TRANSFORMED frame.
 *
 * Extracted from `insertConverter.ts` (where it was private) when the heavy
 * `POLYLINE` converter needed the identical math (@codex PR #785 round 2, F1):
 * `heavyPolylineConverter.ts` is imported BY `insertConverter.ts` for the
 * block-child arm, so it cannot import back from it — dependency-cruiser
 * `no-circular` (CLAUDE.md ПРИОРИТЕТ #2). A LEAF module both consume is the
 * only shape that keeps ONE definition of "map this arc through that affine":
 * a second copy would be free to drift in its sweep convention, which is
 * exactly the class of bug the shared `arcSpansToBeziers` eval was built to
 * prevent.
 *
 * The arc is mapped by evaluating the PARAMETRIC circle and its DERIVATIVE
 * through the affine, so a non-uniform scale yields a true ellipse-arc (never
 * a scalar-radius collapse) and a mirror (negative determinant) flips the
 * traced orientation on its own. RAW world mm, no `applyOffset`.
 */

import { bulgeToArc } from "../../../scene3d/adapters/arcEllipseSpline";
import type { PathSegment, Point2 } from "../cadPrimitive";
import { arcSpansToBeziers } from "./arcBezier";
import { apply, applyLinear, type Affine2D } from "./affine2d";

/**
 * Build a transformed circular arc by mapping the parametric circle through
 * `m`. `ccw` selects the sweep direction in LOCAL space (DXF ARC is always
 * CCW; a CW bulge arc passes `false`). The affine eval then maps both the
 * point and its derivative, so a non-uniform scale yields a true ellipse-arc
 * and a mirror flips the traced orientation automatically.
 */
export function transformedCircle(
  center: Point2,
  radius: number,
  startAngle: number,
  endAngle: number,
  ccw: boolean,
  m: Affine2D,
): { start: Point2; segments: PathSegment[] } {
  // Signed local-space sweep, matching circularArcToBeziers' normaliseSweep.
  let sweep = endAngle - startAngle;
  if (ccw) {
    if (sweep <= 0) sweep += Math.PI * 2;
  } else {
    if (sweep >= 0) sweep -= Math.PI * 2;
  }
  if (sweep === 0) sweep = ccw ? Math.PI * 2 : -Math.PI * 2;
  const evalPoint = (a: number): Point2 =>
    apply(m, {
      x: center.x + radius * Math.cos(a),
      y: center.y + radius * Math.sin(a),
    });
  const evalDeriv = (a: number): Point2 =>
    applyLinear(m, {
      x: -radius * Math.sin(a),
      y: radius * Math.cos(a),
    });
  const { start, segments } = arcSpansToBeziers(
    startAngle,
    sweep,
    evalPoint,
    evalDeriv,
  );
  return { start, segments };
}

/**
 * Append the span `a` → `b` (carrying `a`'s bulge) to `segments`, transformed
 * through `m`. A zero / undecodable bulge is a straight line; a real bulge is
 * decoded to its arc by the shared {@link bulgeToArc} and emitted as exact
 * cubics through {@link transformedCircle}.
 */
export function appendTransformedBulgeSpan(
  segments: PathSegment[],
  a: Point2,
  b: Point2,
  bulge: number,
  m: Affine2D,
): void {
  if (bulge === 0) {
    segments.push({ kind: "line", to: apply(m, { x: b.x, y: b.y }) });
    return;
  }
  const arc = bulgeToArc(a.x, a.y, b.x, b.y, bulge);
  if (!arc) {
    segments.push({ kind: "line", to: apply(m, { x: b.x, y: b.y }) });
    return;
  }
  // Map the local arc through the affine (handles non-uniform / mirror). The
  // bulge decoder already gives the correct start→end angles and CCW flag, so
  // the signed sweep traces from vertex `a` to vertex `b` correctly.
  const { segments: bez } = transformedCircle(
    { x: arc.centerX, y: arc.centerY },
    arc.radius,
    arc.startAngle,
    arc.endAngle,
    arc.ccw,
    m,
  );
  for (const s of bez) segments.push(s);
}
